import * as React from 'react';
import { Badge, Button, InputGroup, Stack, Tab, Tabs } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Attendance,
  BookingViewRow,
  ConvoUserView,
  Event,
  EventRow,
  BookingView,
  EventAttachment,
  EventFeedback,
  InstitutionGroupEvent,
  Ticket,
  UserView,
  EventCoHost,
  EventCoHostRow,
  PaymentIn,
  InstitutionGroupEventRow,
  EventFeedbackAnonymousView,
} from 'cb-model';
import {
  addControlColumn,
  Display,
  General,
  getConfigOrThrow,
  IsAllowed,
  List,
  NavIconContainer,
  QueryMulti,
  Section,
  selectUser,
  UneditableReference,
  useCacheQuery,
  useCacheRecord,
  useDocumentTitle,
} from 'gossamer-front-end';
import {
  displayDateTimeToMinute,
  displayLocaleTime,
  getDisplayTextFromPrimitive,
  getFilter,
  truncate,
} from 'gossamer-unified';
import { EventSharing } from './EventSharing';
import { EventQRCode } from './EventQRCode';
import { EventHelp } from './EventHelp';
import { EventRescheduleModal } from './EventRescheduleModal';
import { EventCancelConfirmModal } from './EventCancelConfirmModal';
import { EventChangeLocationModal } from './EventChangeLocationModal';
import { EventChangeHostModal } from './EventChangeHostModal';
import { EventRepeatPatternModal } from './EventRepeatPatternModal';
import { UnbookUserFromEventButton } from '../user/UnbookUserFromEventButton';
import { BookUserToEventButton } from './BookUserToEventButton';
import { CreateForumConvoModal } from '../convo/CreateForumConvoModal';
import { AddCoHostButton } from './AddCoHostButton';
import { RemoveCoHostButton } from './RemoveCoHostButton';
import { StreamingDestinations } from './stream/StreamingDestinations';
import { Livestream } from './stream/Livestream';
import { CustomInfo } from 'cb-unified';
import { DisplayMarkdown, DownloadButton } from 'shared-front-end';
import { RemoveEventFromGroupButton } from '../institution-group/RemoveEventFromGroupButton';
import { AddGroupToEventButton } from './AddGroupToEventButton';
import { RepeatedEventsSection } from './RepeatedEventsSection';
import { SeriesReconciliationHistorySection } from './SeriesReconciliationHistorySection';

export const EventDisplay = (): React.JSX.Element => {
  const params = useParams();
  const record = useCacheRecord(Event, params.key);
  const user = useSelector(selectUser);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showChangeHost, setShowChangeHost] = React.useState(false);
  const [showRepeatPattern, setShowRepeatPattern] = React.useState(false);
  useDocumentTitle(record.data?.title || 'Event Display');

  const bookingFilters = [getFilter('event_id', 'eq', params.key)];
  if (record.data?.visibility_limit !== 'cancelled') {
    bookingFilters.push(getFilter('booking_status', 'eq', 'active'));
  }

  return (
    <>
      <NavIconContainer entityId={Event.id} />

      {record.data && <EventInfoDisplay data={record.data} />}

      <Tabs
        activeKey={searchParams.get('tab') || 'overview'}
        id="event-display-tabs"
        className="mb-3"
        aria-label="Event display sections"
        variant="underline"
        onSelect={(tab) => {
          if (!tab) {
            return;
          }
          const newParams = new URLSearchParams();
          newParams.set('tab', tab);
          setSearchParams(newParams);
        }}
      >
        <Tab eventKey="overview" title="Overview">
          <Section
            title="Hosts"
            subtitle="Only registered users can act as Main Host or Co-Hosts and control Livestreams"
          >
            {record.data && (
              <div>
                <InputGroup size="sm">
                  <InputGroup.Text>
                    Main Host 👤&nbsp;
                    <UneditableReference
                      refEntity={UserView}
                      value={record.data.main_host_user_id || record.data.created_by}
                    />
                  </InputGroup.Text>
                  <Button variant="secondary" onClick={() => setShowChangeHost(true)}>
                    Change Main Host
                  </Button>
                </InputGroup>
                <EventChangeHostModal
                  eventId={record.data.id}
                  institutionId={record.data.institution_id}
                  mainUserHostId={record.data.main_host_user_id || record.data.created_by}
                  show={showChangeHost}
                  setShow={setShowChangeHost}
                />
                <br />
              </div>
            )}
            <p>Co-Hosts</p>
            <QueryMulti
              entity={EventCoHost}
              alterEntity={(localEntity: typeof EventCoHost) => {
                localEntity.fields.event_id.listColumn = false;
                addControlColumn(
                  localEntity,
                  'remove',
                  999,
                  (row: EventCoHostRow) => String(row.id),
                  RemoveCoHostButton,
                );
              }}
              queryOptions={{
                where: {
                  and: [
                    getFilter('event_id', 'eq', params.key),
                    {
                      or: [
                        getFilter('user_id', 'bl', null),
                        getFilter('user_id', 'ne', record.data?.main_host_user_id || record.data?.created_by),
                      ],
                    },
                  ],
                },
              }}
              render={List}
              showSettingsControl={false}
              showItemCount={true}
            />
            <AddCoHostButton eventId={Number(params.key)} />
          </Section>

          <Section title="Tickets">
            <QueryMulti
              entity={Ticket}
              alterEntity={(localEntity: typeof Ticket) => {
                localEntity.fields.event_id.listColumn = false;
              }}
              queryOptions={{
                where: {
                  and: [getFilter('event_id', 'eq', params.key)],
                },
              }}
              render={List}
            />
          </Section>

          {user.roleId !== 'user' && (
            <Section title="Groups">
              <QueryMulti
                entity={InstitutionGroupEvent}
                alterEntity={(localEntity: typeof InstitutionGroupEvent) => {
                  localEntity.fields.institution_event_id.listColumn = false;
                  addControlColumn(
                    localEntity,
                    'remove',
                    999,
                    (row: InstitutionGroupEventRow) => String(row.id),
                    RemoveEventFromGroupButton,
                  );
                }}
                queryOptions={{
                  where: {
                    and: [getFilter('institution_event_id', 'eq', params.key)],
                  },
                }}
                render={List}
                showSettingsControl={false}
              />
              <AddGroupToEventButton institutionId={record.data?.institution_id || 0} eventId={Number(params.key)} />
            </Section>
          )}
        </Tab>

        <Tab eventKey="bookings" title="Bookings">
          <Section title="Bookings">
            <QueryMulti
              entity={BookingView}
              alterEntity={(localEntity: typeof BookingView) => {
                localEntity.fields.event_id.listColumn = false;
                localEntity.fields.start_at.listColumn = false;
                addControlColumn(
                  localEntity,
                  'remove',
                  999,
                  (row: BookingViewRow) => String(row.ticket_booking_id),
                  UnbookUserFromEventButton,
                );
              }}
              queryOptions={{
                where: {
                  and: bookingFilters,
                },
              }}
              render={List}
            />
            {(user.roleId === 'staff' ||
              (user.roleId === 'admin' && (user.customInfo as CustomInfo)?.adminInstitutionType === 'enterprise')) && (
              <BookUserToEventButton eventId={params.key} />
            )}
          </Section>

          <IsAllowed entity={PaymentIn} action="read">
            <Section title="Payments">
              <QueryMulti
                entity={PaymentIn}
                alterEntity={(localEntity: typeof PaymentIn) => {
                  Object.values(localEntity.fields).forEach((field) => (field.listColumn = false));
                  localEntity.fields.created_at.listColumn = true;
                  localEntity.fields.status.listColumn = true;
                  localEntity.fields.user_id.listColumn = true;
                  localEntity.fields.booking_payment_terms_id.listColumn = true;
                  localEntity.fields.stripe_charge_id.listColumn = true;
                  localEntity.fields.booking_fee.listColumn = true;
                  localEntity.fields.net_amount.listColumn = true;
                }}
                queryOptions={{
                  where: {
                    and: [getFilter('event_id', 'eq', params.key)],
                  },
                }}
                render={List}
              />
            </Section>
          </IsAllowed>

          <Section title="Attendance Records">
            <QueryMulti
              entity={Attendance}
              alterEntity={(localEntity: typeof Attendance) => {
                localEntity.fields.event_id.listColumn = false;
              }}
              queryOptions={{
                where: {
                  and: [getFilter('event_id', 'eq', params.key)],
                },
              }}
              render={List}
            />
          </Section>
        </Tab>

        <Tab eventKey="content-feedback" title="Content &amp; Feedback">
          <Section title="Attachments" subtitle="Files made available to users booked to this event">
            <QueryMulti
              entity={EventAttachment}
              alterEntity={(localEntity: typeof EventAttachment) => {
                localEntity.fields.event_id.listColumn = false;
              }}
              queryOptions={{
                where: {
                  and: [getFilter('event_id', 'eq', params.key)],
                },
              }}
              render={List}
            />
            <IsAllowed entity={EventAttachment} action="update">
              <Link to={`/admin/event/attachments-grid/${params.key}`}>
                <Button size="sm" variant="primary">
                  Update
                </Button>
              </Link>
            </IsAllowed>
          </Section>

          <Section title="Conversations" subtitle="Message threads available to users booked to this event">
            <QueryMulti
              entity={ConvoUserView}
              queryOptions={{
                where: {
                  and: [getFilter('forum_type', 'eq', 'Event'), getFilter('forum_key', 'eq', params.key)],
                },
              }}
              render={List}
            />
            {record.data && (
              <CreateForumConvoModal
                forumType="Event"
                forumKey={params.key}
                institutionId={record.data.institution_id}
              />
            )}
          </Section>

          {user.roleId === 'user' && (
            <Section title="Feedback">
              <QueryMulti
                entity={EventFeedbackAnonymousView}
                alterEntity={(localEntity: typeof EventFeedbackAnonymousView) => {
                  localEntity.fields.event_id.listColumn = false;
                }}
                queryOptions={{
                  where: {
                    and: [getFilter('event_id', 'eq', params.key)],
                  },
                }}
                render={List}
              />
            </Section>
          )}
          {user.roleId !== 'user' && (
            <Section title="Feedback">
              <QueryMulti
                entity={EventFeedback}
                alterEntity={(localEntity: typeof EventFeedback) => {
                  localEntity.fields.event_id.listColumn = false;
                }}
                queryOptions={{
                  where: {
                    and: [getFilter('event_id', 'eq', params.key)],
                  },
                }}
                render={List}
              />
              {!!params.key && (
                <DownloadButton
                  url={[
                    getConfigOrThrow('gossamer-be-origin'),
                    getConfigOrThrow('gossamer-api-path'),
                    'main/feedback/export/event',
                    encodeURIComponent(params.key),
                  ].join('/')}
                >
                  Download feedback CSV
                </DownloadButton>
              )}
              <p>CSV files can be uploaded into spreadsheet applications such as Microsoft Excel or Google Sheets.</p>
            </Section>
          )}
        </Tab>

        {record.data && ['online', 'hybrid'].includes(record.data.location_type) && (
          <Tab eventKey="livestream" title="Livestream">
            <Section title="Livestream">
              <Livestream eventId={record.data.id} livestreamSessionId={record.data.livestream_session_id} />
              <StreamingDestinations livestreamSessionId={record.data.livestream_session_id} />
            </Section>
          </Tab>
        )}

        {record.data?.repeat_pattern && record.data.repeat_group_id && (
          <Tab eventKey="repetition" title="Repetition">
            <Section title="Repetition">
              <Button variant="secondary" size="sm" onClick={() => setShowRepeatPattern(true)}>
                Manage repeat pattern / split series
              </Button>
            </Section>
            <EventRepeatPatternModal
              eventId={record.data.id}
              firstStartAt={record.data.start_at}
              repeatPattern={record.data.repeat_pattern}
              show={showRepeatPattern}
              setShow={setShowRepeatPattern}
            />
            <RepeatedEventsSection currentEventId={record.data.id} repeatGroupId={record.data.repeat_group_id} />
            <SeriesReconciliationHistorySection repeatGroupId={record.data.repeat_group_id} />
          </Tab>
        )}

        {user.roleId === 'staff' && record.data && (
          <Tab eventKey="administration" title="Administration">
            <Section title="Additional Details" subtitle="This view is only available to staff users">
              <Display entity={Event} keyValue={params.key} row={record.data} fieldGroup="staff-view" />
            </Section>
          </Tab>
        )}
      </Tabs>
    </>
  );
};

interface EventInfoDisplayProps {
  data: EventRow;
}

const EventInfoDisplay = (props: EventInfoDisplayProps) => {
  const [showDeleteConfirm, setShowCancelConfirm] = React.useState(false);
  const [showChangeLocation, setShowChangeLocation] = React.useState(false);
  const [showReschedule, setShowReschedule] = React.useState(false);
  const ticketQuery = useCacheQuery(Ticket, {
    where: { and: [getFilter('event_id', 'eq', props.data.id)] },
  });
  const hasNoPaidTickets = !!ticketQuery.data && ticketQuery.data.every((ticket) => ticket.price === 0);
  const isCancelled = props.data.visibility_limit === 'cancelled';
  return (
    <Stack direction="vertical">
      <div>
        <img
          src={props.data.images}
          style={{
            width: '100%',
            height: '20rem',
            objectFit: 'cover',
            borderRadius: 10,
          }}
        />
      </div>
      <br />
      <Section>
        <div
          style={{
            display: 'flex',
            flexFlow: 'row wrap',
            gap: 10,
            marginBottom: 20,
          }}
        >
          <div>
            <Badge pill bg="secondary">
              <General editable={false} field={Event.fields.type} fieldId="type" value={props.data.type} />
            </Badge>
          </div>
          {props.data.type !== 'on-demand' && !isCancelled && (
            <div>
              <InputGroup size="sm">
                <InputGroup.Text>
                  📅&nbsp;
                  {getDisplayTextFromPrimitive(Event.fields.start_at, props.data.start_at)}
                  &nbsp;-&nbsp;{displayLocaleTime(props.data.end_at)}
                </InputGroup.Text>
                <Button variant="secondary" onClick={() => setShowReschedule(true)} disabled={!hasNoPaidTickets}>
                  Reschedule
                </Button>
                {!hasNoPaidTickets && <InputGroup.Text>Not allowed with paid tickets</InputGroup.Text>}
              </InputGroup>
            </div>
          )}
          {props.data.type !== 'on-demand' && !isCancelled && (
            <div>
              <InputGroup size="sm">
                <InputGroup.Text>
                  📌&nbsp;
                  {props.data.location_type === 'online' ? 'online' : truncate(props.data.location, 50)}
                </InputGroup.Text>
                <Button variant="secondary" onClick={() => setShowChangeLocation(true)}>
                  Relocate
                </Button>
              </InputGroup>
            </div>
          )}
          {!isCancelled && (
            <div>
              <Button variant="primary" size="sm" href={`/admin/event/edit/${props.data.id}`}>
                Edit event
              </Button>
            </div>
          )}
          {!isCancelled && (
            <div>
              <Button variant="warning" size="sm" href={`/admin/event/create?copyFrom=${props.data.id}`}>
                Copy event
              </Button>
            </div>
          )}
          {!isCancelled && (
            <div>
              <Button variant="danger" size="sm" onClick={() => setShowCancelConfirm(true)}>
                Cancel event
              </Button>
            </div>
          )}
          {isCancelled && (
            <div>
              <Badge pill bg="danger">
                Cancelled
              </Badge>
            </div>
          )}
          <div style={{ color: 'var(--bs-gray-500)', fontSize: 14, paddingTop: 5 }}>
            Created: {displayDateTimeToMinute(new Date(props.data.created_at))}
          </div>
        </div>
        <h3>{props.data.title}</h3>
        <div>
          <DisplayMarkdown source={props.data.markdown_description} />
        </div>
      </Section>

      <div style={{ display: 'flex', flexFlow: 'row wrap', gap: 20, paddingBottom: 20 }}>
        <EventSharing eventId={props.data.id} eventTitle={props.data.title} />
        {props.data.flow_option !== 'none' && (
          <EventQRCode
            eventTitle={props.data.title}
            attendanceToken={props.data.attendance_token}
            locationType={props.data.location_type}
          />
        )}
        <EventHelp />
      </div>
      {props.data.type !== 'on-demand' && (
        <EventChangeLocationModal
          eventId={props.data.id}
          locationType={props.data.location_type}
          location={props.data.location}
          show={showChangeLocation}
          setShow={setShowChangeLocation}
        />
      )}
      <EventCancelConfirmModal
        eventId={props.data.id}
        isRepeated={!!props.data.repeat_pattern && !!props.data.repeat_group_id}
        show={showDeleteConfirm}
        setShow={setShowCancelConfirm}
      />
      {props.data.type !== 'on-demand' && (
        <EventRescheduleModal
          eventId={props.data.id}
          livestreamSessionId={props.data.livestream_session_id}
          firstStartAt={props.data.start_at}
          firstEndAt={props.data.end_at}
          show={showReschedule}
          setShow={setShowReschedule}
        />
      )}
    </Stack>
  );
};
